import { faGear } from '@fortawesome/pro-solid-svg-icons';
import { useAtom } from 'jotai';
import { ComponentProps } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { MenuItem } from '@growtherapy/sprout-ui';

import { IconMenuItem } from '../../components';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { SettingsDrawerControl } from '../../session';
import { VisitorDrawerState, visitorDrawerStateAtom } from '../state';

export type VisitorSettingsDrawerMenuItemProps = {
  drawerId: string;
  isSettingsDrawerOpen: boolean;
  className?: string;
} & Pick<ComponentProps<typeof MenuItem>, 'onClick'>;

export function VisitorSettingsDrawerControlMenuItem({
  className,
  drawerId,
  isSettingsDrawerOpen,
  onClick,
}: VisitorSettingsDrawerMenuItemProps) {
  const t = useTranslate();
  return (
    <IconMenuItem
      aria-controls={drawerId}
      aria-expanded={isSettingsDrawerOpen}
      onClick={onClick}
      className={className}
      icon={faGear}
    >
      <span className='sr-only'>
        {isSettingsDrawerOpen
          ? t('telehealth-core:settings.closeSettings')
          : t('telehealth-core:settings.openSettings')}
      </span>
      <span aria-hidden>{t('telehealth-core:settings.settings')}</span>
    </IconMenuItem>
  );
}

export function VisitorSettingsDrawerControlMenuItemWrapper(
  props: Pick<VisitorSettingsDrawerMenuItemProps, 'drawerId'>,
) {
  const { isSettingsDrawerOpen, toggleDrawerState } = useVisitorDrawerControls();
  return (
    <VisitorSettingsDrawerControlMenuItem
      drawerId={props.drawerId}
      isSettingsDrawerOpen={isSettingsDrawerOpen}
      onClick={toggleDrawerState}
    />
  );
}

export function VisitorSettingsDrawerButton(props: Pick<VisitorSettingsDrawerMenuItemProps, 'drawerId'>) {
  const { isSettingsDrawerOpen, toggleDrawerState } = useVisitorDrawerControls();
  return (
    <SettingsDrawerControl
      forDrawerStates={VisitorDrawerState.SETTINGS}
      isSettingsDrawerOpen={isSettingsDrawerOpen}
      onClick={toggleDrawerState}
      drawerId={props.drawerId}
    />
  );
}

function useVisitorDrawerControls() {
  const [drawerState, setDrawerState] = useAtom(visitorDrawerStateAtom);
  const isSettingsDrawerOpen = drawerState === VisitorDrawerState.SETTINGS;
  const getDrawerState = useAtomCallbackMemoized((get) => get(visitorDrawerStateAtom), []);
  const toggleDrawerState = () => {
    const currentDrawerState = getDrawerState();
    if (currentDrawerState === VisitorDrawerState.SETTINGS) {
      setDrawerState(null);
      return;
    }
    setDrawerState(VisitorDrawerState.SETTINGS);
  };

  return {
    isSettingsDrawerOpen,
    toggleDrawerState,
  };
}
